import type { SurveyAttribute, SystemUser, User } from '@engagement-web-app/common';
import { format } from 'date-fns';

export function debounce<T>(func: (args: T) => void, wait: number) {
    let timeout: NodeJS.Timeout;
    return (args: T) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(args), wait);
    };
}

export function throttle<T>(func: (args: T) => void, wait: number) {
    let timeout: NodeJS.Timeout | null = null;
    return (args: T) => {
        if (!timeout) {
            timeout = setTimeout(() => {
                timeout = null;
                func(args);
            }, wait);
        }
    };
}

export function formatPostalCode(postalCode: string): string {
    return `〒${postalCode.slice(0, 3)}-${postalCode.slice(3)}`;
}

// TODO: More robust formatting, maybe using some library
export function formatPhoneNumber(phoneNumber: string) {
    const cleaned = phoneNumber.replace(/\D/g, '');
    if (cleaned.length === 10) {
        return cleaned.replace(/(.{2})(.{4})(.{4})/, '$1-$2-$3');
    } else if (cleaned.length === 11) {
        return cleaned.replace(/(.{3})(.{4})(.{4})/, '$1-$2-$3');
    }
    return phoneNumber;
}

export function getFullName(user: SystemUser | User) {
    return [user.lastName, user.firstName].join(' ');
}

// TODO: Discard in favor of the defintion in the common package
export type ScoreKey = 'di' | 'deviation';
export type SortOrder = 'asc' | 'desc';

export function sortByScore<K extends ScoreKey>(key: K, sortOrder: SortOrder) {
    return function sort(a: { [key in K]: number | null }, b: { [key in K]: number | null }) {
        if (a[key] === null && b[key] === null) return 0;
        if (a[key] === null) return sortOrder === 'desc' ? 1 : -1;
        if (b[key] === null) return sortOrder === 'desc' ? -1 : 1;
        return sortOrder === 'desc' ? b[key] - a[key] : a[key] - b[key];
    };
}

export function formatNumber(value: number, decimalPlaces: number = 1): string {
    const { format } = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
    });
    return format(value);
}

/**
 * Format a date as `yyyy年M月` for use as a chart axis / legend label.
 * Accepts either a Date or an ISO 8601 string (the latter is what the
 * backend hands us over JSON).
 */
export function formatDateToLabel(date: string | Date): string {
    return format(new Date(date), 'yyyy年M月');
}

export const metricOptions = [
    { value: 'di', label: 'DI' },
    { value: 'deviation', label: '偏差値' },
] as const;

export type MetricOption = (typeof metricOptions)[number]['value'];

/** Pads an array to the specified length with the given pad value. */
export function padArray<T>(arr: T[], length: number, padValue: T): T[] {
    const padded = [...arr];
    while (padded.length < length) {
        padded.push(padValue);
    }
    return padded;
}

export function calculateDelta(value: number, previousValue: number, decimalPlaces = 1): number {
    const delta = value - previousValue;
    return Math.round(delta * 10 ** decimalPlaces) / 10 ** decimalPlaces;
}

/**
 * Group a flat list of selected option ids by their parent attribute (dimension),
 * using the survey's attribute settings as the source of truth. Returns the
 * request shape expected by `fetchSurveyScoresBy{Category,Question}XAttributes`.
 * Unknown option ids and attributes with no matching options are dropped.
 */
export function groupAttributeOptionIdsBySetting(optionIds: readonly string[], attributes: readonly SurveyAttribute[]) {
    if (attributes.length === 0 || optionIds.length === 0) return [];

    return attributes
        .map((attribute) => ({
            // TODO: Rename to `settingId`
            attributeId: attribute.id,
            optionIds: attribute.options.map((v) => v.id).filter((id) => optionIds.includes(id)),
        }))
        .filter((entry) => entry.optionIds.length > 0);
}

type ErrorSource = { isError: boolean; error: Error | null; refetch: () => void };

/**
 * Combines several (possibly dependent) queries into a single error/retry pair.
 * Picks the first query in error, in the given order — pass upstream queries
 * first, since retrying a downstream query that's disabled because an upstream
 * one failed would be a no-op. Once the upstream query is retried successfully,
 * TanStack Query re-enables and fires the downstream one automatically.
 */
export function combineQueryErrors(...queries: ErrorSource[]) {
    const failed = queries.find((q) => q.isError);
    return {
        isError: failed !== undefined,
        error: failed?.error ?? null,
        retry: () => failed?.refetch(),
    };
}
